import { useQueryClient } from "@tanstack/react-query";
import { Loader2, UserRound } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthenticateUser } from "~/api/authentication/authentication";
import { getGetCurrentUserQueryKey } from "~/api/users/users";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  removeAllTokens,
  ROUTES,
  storeAccessToken,
  storeRefreshToken,
  storeUsername,
  validateEmail,
} from "~/helpers";
import { useThemedOnboardingImage } from "~/hooks/useThemedOnboardingImage";
import { trackEvent } from "~/utils/analytics";
import { initiateOAuthLogin, SocialProvider } from "~/utils/auth/socialAuth";
import { useAuthStepImage } from "../hooks/useAuthStepImage";

interface FormErrors {
  email?: boolean;
  password?: boolean;
}

export const LoginForm = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [isNavigating, setIsNavigating] = useState(false);
  const { mutate, isPending, isError } = useAuthenticateUser();
  const queryClient = useQueryClient();

  const createAccountBg = useThemedOnboardingImage("create-account");
  useAuthStepImage(createAccountBg);

  useEffect(() => {
    const username = searchParams.get("username");
    if (username) {
      const usernameWithPlus = username.replace(/ /g, "+");
      setEmail(decodeURIComponent(usernameWithPlus));
    }

    if (searchParams.get("guest") === "true") {
      removeAllTokens();
      queryClient.setQueryData(getGetCurrentUserQueryKey(), null);
    }
  }, [searchParams, queryClient]);

  const handleSocialLogin = (provider: SocialProvider) => {
    trackEvent({
      eventName: `app/login/social_login.${provider.toLowerCase()}/click`,
    });
    initiateOAuthLogin(provider);
  };

  if (isNavigating) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="text-sm text-muted-foreground">
          <FormattedMessage
            id="loginForm.signingIn"
            defaultMessage="Signing in..."
          />
        </p>
      </div>
    );
  }

  const validateForm = () => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!validateEmail(email)) {
      newErrors.email = true;
      isValid = false;
    }

    if (!password.trim()) {
      newErrors.password = true;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const onLoginFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    removeAllTokens();
    queryClient.setQueryData(getGetCurrentUserQueryKey(), null);
    queryClient.invalidateQueries();

    trackEvent({ eventName: "app/login/log_in.button/submit" });
    mutate(
      { data: { email, password } },
      {
        onSuccess: async (data) => {
          if (
            "challengeName" in data &&
            data.challengeName === "NEW_PASSWORD_REQUIRED"
          ) {
            navigate(ROUTES.CHANGE_PASSWORD, {
              state: {
                session: "session" in data ? data.session : undefined,
                username: "username" in data ? data.username : undefined,
                currentPassword: password,
              },
            });
            return;
          }

          if ("accessToken" in data && "refreshToken" in data) {
            storeAccessToken(data.accessToken);
            storeRefreshToken(data.refreshToken);
            storeUsername(data.username);
            trackEvent({ eventName: "app/login/success" });
            setIsNavigating(true);

            try {
              // Invalidate and refetch user data before navigating
              // This ensures AppLayout has the user data in cache and won't redirect to login
              await queryClient.invalidateQueries({
                queryKey: getGetCurrentUserQueryKey(),
              });
              await queryClient.refetchQueries({
                queryKey: getGetCurrentUserQueryKey(),
              });
            } catch {
              // If refetch fails, still navigate - AppLayout will handle the auth check
            }

            const retpath = searchParams.get("retpath");
            navigate(retpath ? decodeURIComponent(retpath) : "/");
          } else {
            throw new Error("No access token or refresh token");
          }
        },
        onError: () => {
          trackEvent({ eventName: "app/login/error" });
        },
      }
    );
  };

  return (
    <div className="flex justify-center items-start">
      <form onSubmit={onLoginFormSubmit} className="w-full max-w-[560px]">
        <UserRound className="h-[32px] w-[32px] text-[#71717A] mb-4 sm:mb-[24px]" />
        <h3 className="text-2xl font-semibold text-base-foreground pt-2 sm:pt-[16px]">
          <FormattedMessage id="loginForm.title" defaultMessage="Login" />
        </h3>
        <p className="text-sm text-[#71717A] mt-[16px] mb-[24px]">
          <FormattedMessage
            id="loginForm.subtitle"
            defaultMessage="Please enter your email address and full name to create a Sky AI account."
          />
        </p>
        <div className="mb-4">
          <Label
            htmlFor="loginUsername"
            className="block text-sm font-medium text-base-foreground mb-2 leading-none"
          >
            <FormattedMessage id="loginForm.username" defaultMessage="Email" />
          </Label>
          <Input
            id="loginUsername"
            placeholder={intl.formatMessage({
              id: "loginForm.usernamePlaceholder",
              defaultMessage: "Input your email",
            })}
            value={email}
            className={`w-full px-3 py-2 border rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 shadow-none ${
              errors.email || isError ? "border-red-600" : "border-gray-300"
            }`}
            onChange={(e) => setEmail(e.target.value)}
          />
          {errors.email && (
            <span className="text-red-600 text-sm mt-2 block">
              <FormattedMessage
                id="auth.register.emailError"
                defaultMessage="Email should be <strong>example@site.com</strong>"
                values={{
                  strong: (chunks) => (
                    <strong className="font-semibold">{chunks}</strong>
                  ),
                }}
              />
            </span>
          )}
        </div>
        <div>
          <Label
            htmlFor="loginPassword"
            className="block text-sm font-medium text-base-foreground mb-2 leading-none"
          >
            <FormattedMessage
              id="loginForm.password"
              defaultMessage="Password"
            />
          </Label>
          <Input
            id="loginPassword"
            type="password"
            placeholder={intl.formatMessage({
              id: "loginForm.passwordPlaceholder",
              defaultMessage: "Input your password",
            })}
            value={password}
            className={`w-full px-3 py-2 border rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 shadow-none ${
              errors.password || isError ? "border-red-600" : "border-gray-300"
            }`}
            onChange={(e) => setPassword(e.target.value)}
          />
          {errors.password && (
            <span className="text-red-600 text-sm mt-2 block">
              <FormattedMessage
                id="loginForm.passwordRequired"
                defaultMessage="Please enter the password"
              />
            </span>
          )}
          {isError && (
            <span className="text-red-600 text-sm mt-2 block">
              <FormattedMessage
                id="loginForm.errorIncorrect"
                defaultMessage="Email or password incorrect"
              />
            </span>
          )}
        </div>
        <div className="text-sm pt-[16px] pb-[24px]">
          <Link
            to={ROUTES.FORGOT_PASSWORD}
            onClick={() =>
              trackEvent({ eventName: "app/login/forgot_password.link/click" })
            }
            className="text-blue-600 hover:text-blue-700 font-semibold"
          >
            <FormattedMessage
              id="loginForm.forgotPassword"
              defaultMessage="Forgot password?"
            />
          </Link>
        </div>
        <div className="flex flex-col gap-4">
          <Button className="w-full" type="submit" disabled={isPending}>
            <FormattedMessage id="loginForm.login" defaultMessage="Log In" />
          </Button>
          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                <FormattedMessage id="auth.register.or" defaultMessage="or" />
              </span>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => handleSocialLogin("Google")}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            <FormattedMessage
              id="loginForm.continueWithGoogle"
              defaultMessage="Continue with Google"
            />
          </Button>
          <Button variant="secondary" asChild>
            <Link
              to={
                searchParams.get("guest") === "true"
                  ? `${ROUTES.REGISTER}?guest=true`
                  : ROUTES.REGISTER
              }
            >
              <FormattedMessage
                id="auth.register.createAccount"
                defaultMessage="Create Account"
              />
            </Link>
          </Button>
        </div>
      </form>
    </div>
  );
};
